import { makeAutoObservable } from "mobx";
import RootStore from "stores/RootStore";

// Types
export interface Order {
  id: number;
  orderNumber: string;
  customerName: string;
  company: string;
  mixDesign: string;
  totalYards: number;
  deliveryDate: string;
  deliveryTime: string;
  deliveryAddress: string;
  status: 'scheduled' | 'in_progress' | 'delivered' | 'cancelled';
  estimatedTotal: number;
  loadsCompleted: number;
  totalLoads: number;
  createdAt: string;
}

// Mock data
const MOCK_ORDERS: Order[] = [
  { id: 1, orderNumber: 'ORD-241209-A1B2', customerName: 'John Smith', company: 'Smith Construction', mixDesign: '3000 PSI Standard', totalYards: 30, deliveryDate: '2024-12-10', deliveryTime: '07:00', deliveryAddress: '123 Main St, Austin, TX 78701', status: 'scheduled', estimatedTotal: 3750, loadsCompleted: 0, totalLoads: 3, createdAt: '2024-12-09' },
  { id: 2, orderNumber: 'ORD-241209-C3D4', customerName: 'Maria Garcia', company: 'Garcia Builders', mixDesign: '4000 PSI Commercial', totalYards: 50, deliveryDate: '2024-12-10', deliveryTime: '08:00', deliveryAddress: '456 Oak Ave, Austin, TX 78702', status: 'in_progress', estimatedTotal: 7250, loadsCompleted: 2, totalLoads: 5, createdAt: '2024-12-09' },
  { id: 3, orderNumber: 'ORD-241208-E5F6', customerName: 'Robert Johnson', company: 'Johnson & Sons', mixDesign: '5000 PSI Heavy Duty', totalYards: 20, deliveryDate: '2024-12-09', deliveryTime: '06:30', deliveryAddress: '789 Pine Rd, Round Rock, TX 78664', status: 'delivered', estimatedTotal: 3700, loadsCompleted: 2, totalLoads: 2, createdAt: '2024-12-08' },
  { id: 4, orderNumber: 'ORD-241210-G7H8', customerName: 'Sarah Williams', company: 'Williams Contractors', mixDesign: '3000 PSI Standard', totalYards: 15, deliveryDate: '2024-12-11', deliveryTime: '09:00', deliveryAddress: '321 Elm St, Cedar Park, TX 78613', status: 'scheduled', estimatedTotal: 1875, loadsCompleted: 0, totalLoads: 2, createdAt: '2024-12-10' },
  { id: 5, orderNumber: 'ORD-241207-I9J0', customerName: 'Michael Brown', company: 'Brown Development', mixDesign: 'Pump Mix 4000', totalYards: 80, deliveryDate: '2024-12-08', deliveryTime: '05:30', deliveryAddress: '654 Maple Dr, Georgetown, TX 78626', status: 'delivered', estimatedTotal: 12800, loadsCompleted: 8, totalLoads: 8, createdAt: '2024-12-07' },
  { id: 6, orderNumber: 'ORD-241210-K1L2', customerName: 'John Smith', company: 'Smith Construction', mixDesign: '4000 PSI Commercial', totalYards: 40, deliveryDate: '2024-12-11', deliveryTime: '07:30', deliveryAddress: '123 Main St, Austin, TX 78701', status: 'scheduled', estimatedTotal: 5800, loadsCompleted: 0, totalLoads: 4, createdAt: '2024-12-10' },
  { id: 7, orderNumber: 'ORD-241206-M3N4', customerName: 'Lisa Anderson', company: 'Anderson Concrete Works', mixDesign: '4500 PSI High Strength', totalYards: 65, deliveryDate: '2024-12-07', deliveryTime: '06:00', deliveryAddress: '999 Industrial Blvd, Austin, TX 78745', status: 'delivered', estimatedTotal: 10725, loadsCompleted: 7, totalLoads: 7, createdAt: '2024-12-06' },
  { id: 8, orderNumber: 'ORD-241211-O5P6', customerName: 'David Martinez', company: 'Martinez Builders', mixDesign: '3000 PSI Standard', totalYards: 25, deliveryDate: '2024-12-12', deliveryTime: '08:30', deliveryAddress: '555 Commerce St, Pflugerville, TX 78660', status: 'scheduled', estimatedTotal: 3125, loadsCompleted: 0, totalLoads: 3, createdAt: '2024-12-11' },
];

const STATUS_LABELS: Record<string, string> = {
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

class OrdersListStore {
  rootStore: RootStore;

  orders: Order[] = [...MOCK_ORDERS];
  searchQuery = '';
  selectedStatus = 'all';
  selectedDateRange = 'all'; // all, today, week, month
  loading = false;
  error: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Computed: Filtered orders
  get filteredOrders(): Order[] {
    let result = this.orders;

    // Filter by status
    if (this.selectedStatus !== 'all') {
      result = result.filter(o => o.status === this.selectedStatus);
    }

    // Filter by date range
    if (this.selectedDateRange !== 'all') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      result = result.filter(o => {
        const orderDate = new Date(o.deliveryDate);
        orderDate.setHours(0, 0, 0, 0);

        if (this.selectedDateRange === 'today') {
          return orderDate.getTime() === today.getTime();
        } else if (this.selectedDateRange === 'week') {
          const weekAhead = new Date(today);
          weekAhead.setDate(weekAhead.getDate() + 7);
          return orderDate >= today && orderDate <= weekAhead;
        } else if (this.selectedDateRange === 'month') {
          const monthAhead = new Date(today);
          monthAhead.setMonth(monthAhead.getMonth() + 1);
          return orderDate >= today && orderDate <= monthAhead;
        }
        return true;
      });
    }

    // Filter by search query
    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      result = result.filter(o =>
        o.orderNumber.toLowerCase().includes(query) ||
        o.customerName.toLowerCase().includes(query) ||
        o.company.toLowerCase().includes(query) ||
        o.mixDesign.toLowerCase().includes(query)
      );
    }

    // Sort by delivery date (ascending)
    return result.sort((a, b) => {
      const dateA = new Date(a.deliveryDate + 'T' + a.deliveryTime);
      const dateB = new Date(b.deliveryDate + 'T' + b.deliveryTime);
      return dateA.getTime() - dateB.getTime();
    });
  }

  // Computed: Orders by status
  get ordersByStatus() {
    return {
      scheduled: this.orders.filter(o => o.status === 'scheduled'),
      inProgress: this.orders.filter(o => o.status === 'in_progress'),
      delivered: this.orders.filter(o => o.status === 'delivered'),
      cancelled: this.orders.filter(o => o.status === 'cancelled'),
    };
  }

  // Computed: Today's orders
  get todaysOrders(): Order[] {
    const today = new Date().toISOString().split('T')[0];
    return this.orders.filter(o => o.deliveryDate === today);
  }

  // Computed: Stats
  get stats() {
    const scheduled = this.ordersByStatus.scheduled;
    const inProgress = this.ordersByStatus.inProgress;
    const delivered = this.ordersByStatus.delivered;

    return {
      totalOrders: this.orders.length,
      scheduledCount: scheduled.length,
      inProgressCount: inProgress.length,
      deliveredCount: delivered.length,
      scheduledYards: scheduled.reduce((sum, o) => sum + o.totalYards, 0),
      inProgressYards: inProgress.reduce((sum, o) => sum + o.totalYards, 0),
      deliveredYards: delivered.reduce((sum, o) => sum + o.totalYards, 0),
      totalRevenue: this.orders.reduce((sum, o) => sum + o.estimatedTotal, 0),
      todaysOrders: this.todaysOrders.length,
      todaysYards: this.todaysOrders.reduce((sum, o) => sum + o.totalYards, 0),
    };
  }

  get statusLabels() {
    return STATUS_LABELS;
  }

  // Actions
  setSearchQuery = (query: string) => {
    this.searchQuery = query;
  };

  setSelectedStatus = (status: string) => {
    this.selectedStatus = status;
  };

  setSelectedDateRange = (range: string) => {
    this.selectedDateRange = range;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  reset = () => {
    this.searchQuery = '';
    this.selectedStatus = 'all';
    this.selectedDateRange = 'all';
    this.loading = false;
    this.error = null;
  };
}

export default OrdersListStore;
